/**
 * DemoOneEngage — Internal UAT: Journey Validation View
 * Route: /demo/one-engage (CAPABILITY-GATED — requires crm.lead.read via CapabilityRoute)
 *
 * Purpose: Verify that a completed BuildMyEvent journey is correctly persisted in the CRM.
 * All displayed data is retrieved from authoritative backend APIs.
 *
 * Restrictions (strictly enforced):
 *   - No synthetic fallback data, hardcoded customer names, or fabricated timestamps.
 *   - No raw JSON payload exposure (use the CRM Lead Detail page for technical details).
 *   - If no real journey session exists: empty state with lookup bar and Build My Event link.
 *   - Activity timeline sourced exclusively from journey_service.py (real DB records).
 *   - Session/Lead lookup uses authenticated, tenant-scoped backend endpoints only.
 *   - Session ID is not an authentication credential; user must already hold crm.lead.read.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft, Globe, ArrowRight, CheckCircle2,
  AlertTriangle, RefreshCw, ExternalLink, Clock, Info,
  Calendar, Search,
} from 'lucide-react';
import {
  PJ, getServiceLabel, getCityDisplay, BUDGET_RANGES, GUEST_RANGES,
  VENUE_STATUSES, WEDDING_FUNCTIONS, STYLE_OPTIONS, EVENT_TYPES,
  formatEventDateShort,
} from '../journey/journeyConfig';
import { loadJourneyFromStorage, computeCompletionScore } from '../journey/journeyEngine';
import { JourneyTimeline } from '../components/JourneyTimeline';

const CRM_SYNC_KEY = 'prashasta_crm_sync';
const API          = process.env.REACT_APP_BACKEND_URL;

// ── Human-readable source display labels ─────────────────────────────────────
// Backend taxonomy values (CUSTOMER_CONFIRMED, SYSTEM_DEFAULT, …) are preserved
// unchanged in the database and API contracts. Only the UI display label is corrected.
const SOURCE_DISPLAY_LABELS = {
  CUSTOMER_CONFIRMED:      'Customer Selected',
  SYSTEM_DEFAULT:          'Suggested Default',
  INSPIRATION_SUGGESTED:   'Inspiration Pick',
  AGENT_RECOMMENDED:       'Recommended',
  COMMUNICATION_EXTRACTED: 'From Conversation',
  SYSTEM_DERIVED:          'System Derived',
};
function getSourceLabel(source) {
  return SOURCE_DISPLAY_LABELS[source] || (source || '').replace(/_/g, ' ');
}

const DEMO_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes pjFadeSlideIn { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
  @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
  .demo-fade { animation: pjFadeSlideIn 0.4s ease both; }
`;

// ── Structured Field ───────────────────────────────────────────────────────────
function StructuredField({ label, value, source, color }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'3px', padding:'10px 0', borderBottom:`1px solid ${PJ.sand}` }}>
      <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>
        {label}
      </p>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
        <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.9rem', fontWeight:500, color:color || PJ.obsidian }}>
          {value}
        </p>
        {source && (
          <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.62rem', fontWeight:600, letterSpacing:'0.06em', color:PJ.slate, textTransform:'uppercase' }}>
            · {getSourceLabel(source)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Completion Bar ────────────────────────────────────────────────────────────
function CompletionBar({ pct, isComplete }) {
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'6px' }}>
        <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em' }}>
          {isComplete ? 'Questionnaire Complete' : 'Questionnaire Progress'}
        </p>
        <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', fontWeight:700, color:PJ.obsidian }}>
          {pct}%
        </p>
      </div>
      <div style={{ background:PJ.sand, borderRadius:'100px', height:'6px', overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius:'100px', background:pct>=80?PJ.forest:pct>=50?PJ.gold:PJ.slate, width:`${pct}%`, transition:'width 1s ease' }} />
      </div>
      {isComplete && (
        <p style={{ margin:'6px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate }}>
          All questionnaire sections completed. Venue booking, event confirmation and payments are separate operational steps.
        </p>
      )}
    </div>
  );
}

// ── Source Attribution ────────────────────────────────────────────────────────
function SourceAttributionCard({ state }) {
  const channel    = state?.source_channel     || 'WEBSITE';
  const touchpoint = state?.source_touchpoint  || 'BUILD_MY_EVENT';
  const entryPoint = state?.source_entry_point || 'DIRECT';
  const path       = state?.journey_path || [channel];
  const utms = [
    { key:'Campaign', value:state?.source_campaign },
    { key:'Medium',   value:state?.source_medium   },
    { key:'Term',     value:state?.source_term      },
    { key:'Content',  value:state?.source_content   },
  ].filter(u => u.value);
  const pathLabels = { WEBSITE:'Website', DATE_CHECK:'Date Check', BUILD_MY_EVENT:'Build My Event', WHATSAPP:'WhatsApp Planner', DIRECT:'Direct' };
  return (
    <div style={{ background:PJ.white, borderRadius:'20px', padding:'20px 24px', border:`1px solid ${PJ.sand}` }}>
      <p style={{ margin:'0 0 16px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>
        Acquisition Source
      </p>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' }}>
        <div style={{ width:'30px', height:'30px', borderRadius:'8px', background:`${PJ.gold}15`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <Globe size={14} style={{ color:PJ.gold }} />
        </div>
        <div>
          <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.7rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:PJ.slate }}>Channel</p>
          <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.88rem', fontWeight:600, color:PJ.obsidian }}>
            {pathLabels[channel] || channel} — {pathLabels[touchpoint] || touchpoint}
          </p>
        </div>
        <span style={{ marginLeft:'auto', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'3px 8px', borderRadius:'100px', background:`${PJ.forest}12`, color:PJ.forest, border:`1px solid ${PJ.forest}30` }}>
          DIGITAL
        </span>
      </div>
      {path.length > 0 && (
        <div style={{ display:'flex', alignItems:'center', gap:'6px', flexWrap:'wrap', marginBottom:'12px' }}>
          {path.map((entry, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
              <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.75rem', padding:'3px 10px', borderRadius:'100px', background:i===path.length-1?`${PJ.ruby}12`:PJ.ivory, border:`1px solid ${i===path.length-1?PJ.ruby+'40':PJ.sand}`, color:i===path.length-1?PJ.ruby:PJ.slate, fontWeight:i===path.length-1?600:400 }}>
                {pathLabels[entry] || entry}
              </span>
              {i < path.length - 1 && <ArrowRight size={10} style={{ color:PJ.sand, flexShrink:0 }} />}
            </div>
          ))}
        </div>
      )}
      <div style={{ display:'flex', gap:'6px', marginBottom:utms.length>0?'12px':0 }}>
        <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate }}>Entry:</span>
        <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', fontWeight:600, color:PJ.obsidian }}>{pathLabels[entryPoint] || entryPoint}</span>
        {state?.source_first_seen && (
          <>
            <span style={{ color:PJ.sand, fontSize:'0.72rem' }}>·</span>
            <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate }}>
              {new Date(state.source_first_seen).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit' })}
            </span>
          </>
        )}
      </div>
      {utms.length > 0 && (
        <div style={{ borderTop:`1px solid ${PJ.sand}`, paddingTop:'12px', display:'flex', flexWrap:'wrap', gap:'6px' }}>
          {utms.map(({ key, value }) => (
            <span key={key} style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.68rem', padding:'2px 8px', borderRadius:'100px', background:`${PJ.gold}10`, border:`1px solid ${PJ.gold}30`, color:PJ.slate }}>
              {key}: {value}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CRM Sync Status Badge ─────────────────────────────────────────────────────
function CrmSyncBadge({ syncState, leadId }) {
  if (!syncState) return null;
  const isSynced  = syncState.crm_sync_status === 'SYNCED';
  const isPending = syncState.crm_sync_status === 'PENDING';
  return (
    <div data-testid="demo-crm-sync-badge" style={{ background:isSynced?`${PJ.forest}08`:isPending?`${PJ.gold}08`:`${PJ.ruby}08`, border:`1px solid ${isSynced?PJ.forest+'30':isPending?PJ.gold+'30':PJ.ruby+'30'}`, borderRadius:'12px', padding:'12px 16px', display:'flex', alignItems:'flex-start', gap:'10px' }}>
      <div style={{ flexShrink:0, marginTop:'2px' }}>
        {isSynced ? <CheckCircle2 size={16} style={{ color:PJ.forest }} /> : isPending ? <Clock size={16} style={{ color:PJ.gold }} /> : <AlertTriangle size={16} style={{ color:PJ.ruby }} />}
      </div>
      <div style={{ flex:1 }}>
        <p style={{ margin:'0 0 2px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', fontWeight:700, color:isSynced?PJ.forest:isPending?PJ.gold:PJ.ruby }}>
          {isSynced ? 'CRM Lead Created — Persisted' : isPending ? 'CRM Sync Pending' : 'CRM Sync Failed'}
        </p>
        {isSynced && syncState.lead_number && (
          <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate }}>
            Lead: {syncState.lead_number}
            {leadId && (
              <Link to={`/engage/leads/${leadId}`} style={{ marginLeft:'8px', color:PJ.ruby, textDecoration:'none', fontSize:'0.72rem' }}>
                Open in CRM <ExternalLink size={10} style={{ display:'inline', marginLeft:'2px' }} />
              </Link>
            )}
          </p>
        )}
        {isSynced && (
          <p style={{ margin:'4px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.68rem', color:PJ.slate, fontFamily:'monospace' }}>
            Session: {syncState.journey_session_id}
          </p>
        )}
        {!isSynced && !isPending && (
          <p style={{ margin:'4px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.68rem', color:PJ.slate }}>
            Session captured but not persisted to CRM. Data shown reflects session state only.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
function EmptyState({ navigate }) {
  return (
    <div data-testid="demo-empty-state" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', padding:'48px 24px', textAlign:'center' }}>
      <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:`${PJ.gold}15`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'20px' }}>
        <Calendar size={24} style={{ color:PJ.gold }} />
      </div>
      <p style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'1.5rem', fontWeight:400, color:PJ.obsidian, margin:'0 0 8px' }}>
        No Active Event Journey Found
      </p>
      <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.875rem', color:PJ.slate, maxWidth:'400px', lineHeight:1.65, margin:'0 0 28px' }}>
        This view only displays data from real customer sessions. Complete a Build My Event journey and reach the confirmation step to create an authoritative lead record.
      </p>
      <button
        data-testid="demo-go-to-bme"
        onClick={() => navigate('/build-my-event')}
        style={{ display:'inline-flex', alignItems:'center', gap:'8px', background:PJ.obsidian, color:PJ.white, border:'none', borderRadius:'100px', padding:'12px 24px', fontFamily:"'DM Sans', sans-serif", fontWeight:600, fontSize:'0.875rem', cursor:'pointer' }}
      >
        Go to Build My Event <ArrowRight size={15} />
      </button>
    </div>
  );
}

// ── Session-Only Warning ──────────────────────────────────────────────────────
function SessionOnlyNotice() {
  return (
    <div style={{ display:'flex', gap:'10px', background:`${PJ.gold}08`, border:`1px solid ${PJ.gold}30`, borderRadius:'12px', padding:'12px 16px' }}>
      <Info size={15} style={{ color:PJ.gold, flexShrink:0, marginTop:'2px' }} />
      <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', color:PJ.slate, lineHeight:1.6 }}>
        <strong style={{ color:PJ.obsidian }}>Session data — not yet verified against CRM.</strong>{' '}
        This journey was captured in browser session storage but has not been confirmed as persisted to the database. Complete the Build My Event journey through to the confirmation step to create a verified lead record.
      </p>
    </div>
  );
}

// ── Journey Lookup Bar ────────────────────────────────────────────────────────
// Accepts Lead ID (lead-bme-...) or Journey Session ID.
// Resolves via authenticated, tenant-scoped backend endpoints only.
// Session ID is not an authentication credential — user must already be authenticated.
function LookupBar({ onResult, onError }) {
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);

  const isLeadId = (v) => v.startsWith('lead-') || v.startsWith('LEAD-');

  const handleLookup = async (e) => {
    e.preventDefault();
    const query = input.trim();
    if (!query) return;
    setLoading(true);
    onError(null);
    try {
      let leadId;
      if (isLeadId(query)) {
        // Direct lead ID lookup
        leadId = query;
      } else {
        // Session ID → resolve to lead ID (authenticated, tenant-scoped)
        const res = await axios.get(`${API}/api/leads/session/${encodeURIComponent(query)}`, { withCredentials: true });
        leadId = res.data.lead_id;
      }
      // Fetch full lead + timeline in parallel
      const [leadRes, tlRes] = await Promise.all([
        axios.get(`${API}/api/leads/${leadId}`, { withCredentials: true }),
        axios.get(`${API}/api/leads/${leadId}/journey/timeline`, { withCredentials: true }),
      ]);
      onResult({ lead: leadRes.data, timeline: tlRes.data, leadId });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) onError('Journey not found. Verify the ID belongs to your organisation.');
      else if (status === 403 || status === 401) onError('Access denied. You need crm.lead.read to use this lookup.');
      else onError('Lookup failed. Check the ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      data-testid="demo-lookup-form"
      onSubmit={handleLookup}
      style={{ background:PJ.white, borderRadius:'16px', padding:'16px 20px', border:`1px solid ${PJ.sand}`, display:'flex', gap:'10px', alignItems:'center' }}
    >
      <Search size={15} style={{ color:PJ.slate, flexShrink:0 }} />
      <input
        data-testid="demo-lookup-input"
        value={input}
        onChange={e => setInput(e.target.value)}
        placeholder="Enter Lead ID (lead-bme-…) or Journey Session ID"
        style={{ flex:1, border:'none', outline:'none', fontFamily:"'DM Sans', sans-serif", fontSize:'0.85rem', color:PJ.obsidian, background:'transparent', minWidth:0 }}
      />
      <button
        data-testid="demo-lookup-submit"
        type="submit"
        disabled={loading || !input.trim()}
        style={{ flexShrink:0, background:input.trim()?PJ.obsidian:`${PJ.obsidian}40`, color:PJ.white, border:'none', borderRadius:'100px', padding:'7px 16px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', fontWeight:600, cursor:input.trim()?'pointer':'default', display:'flex', alignItems:'center', gap:'6px', transition:'background 0.2s' }}
      >
        {loading ? <RefreshCw size={12} style={{ animation:'spin 1s linear infinite' }} /> : <Search size={12} />}
        {loading ? 'Looking up…' : 'Look up'}
      </button>
    </form>
  );
}

// ── Real Activity Timeline ────────────────────────────────────────────────────
// Displays ONLY persisted activity records from journey_service.py.
// Questionnaire field selections are captured as a single LEAD_CREATED event —
// individual field selection times are NOT separately recorded or displayed here.
function RealTimeline({ loading, timeline }) {
  if (loading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'80px' }}>
        <RefreshCw size={18} style={{ color:PJ.slate, animation:'spin 1s linear infinite' }} />
      </div>
    );
  }
  const activities = timeline?.activities || [];
  if (!activities.length) {
    return (
      <div data-testid="timeline-empty" style={{ padding:'28px 24px', textAlign:'center' }}>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.85rem', color:PJ.slate, margin:'0 0 6px' }}>No recorded activity yet.</p>
        <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.75rem', color:PJ.slate }}>Activity records appear here once lead actions are logged in the CRM.</p>
      </div>
    );
  }
  return (
    <div style={{ height:'380px', overflow:'hidden' }} data-testid="real-timeline-wrapper">
      <JourneyTimeline activities={activities} loading={false} />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DemoOneEngage() {
  const navigate = useNavigate();

  // ── localStorage-based session (user's own active journey) ───────────────
  const [sessionState,    setSessionState]    = useState(null);
  const [crmSync,         setCrmSync]         = useState(null);
  const [lead,            setLead]            = useState(null);
  const [timeline,        setTimeline]        = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [apiError,        setApiError]        = useState(null);
  const [pageLoading,     setPageLoading]     = useState(true);

  // ── Manual lookup result (internal ID/session lookup by authorized user) ─
  const [lookupResult, setLookupResult] = useState(null); // { lead, timeline, leadId }
  const [lookupError,  setLookupError]  = useState(null);

  // Load session + CRM sync state from localStorage
  useEffect(() => {
    const session = loadJourneyFromStorage();
    const rawSync = localStorage.getItem(CRM_SYNC_KEY);
    const sync    = rawSync ? (() => { try { return JSON.parse(rawSync); } catch { return null; } })() : null;
    setSessionState(session);
    setCrmSync(sync);
    setPageLoading(false);
  }, []);

  // Fetch authoritative data from backend when localStorage session is CRM-synced
  useEffect(() => {
    const crm_lead_id = crmSync?.crm_lead_id;
    if (!crm_lead_id) return;
    setTimelineLoading(true);
    Promise.all([
      axios.get(`${API}/api/leads/${crm_lead_id}`, { withCredentials: true }),
      axios.get(`${API}/api/leads/${crm_lead_id}/journey/timeline`, { withCredentials: true }),
    ])
      .then(([leadRes, tlRes]) => {
        setLead(leadRes.data);
        setTimeline(tlRes.data);
        setApiError(null);
      })
      .catch(err => {
        const status = err?.response?.status;
        if (status === 403 || status === 401) setApiError('Access denied. You need crm.lead.read to view this journey.');
        else if (status === 404) setApiError(`Lead record ${crm_lead_id} not found in CRM.`);
        else setApiError('Unable to load CRM data. Check backend connectivity.');
      })
      .finally(() => setTimelineLoading(false));
  }, [crmSync]);

  // Handle manual lookup result
  const handleLookupResult = useCallback(({ lead: l, timeline: tl, leadId }) => {
    setLookupResult({ lead: l, timeline: tl, leadId });
    setLookupError(null);
  }, []);

  // ── Active data: prefer lookup result over localStorage when available ────
  const activeLead       = lookupResult?.lead     ?? lead;
  const activeTimeline   = lookupResult?.timeline  ?? timeline;
  const activeLeadId     = lookupResult?.leadId    ?? crmSync?.crm_lead_id;
  const isLookupMode     = !!lookupResult;
  const isSynced         = isLookupMode || crmSync?.crm_sync_status === 'SYNCED';
  const s                = sessionState; // localStorage session data for display fields

  // ── Derived display values (from localStorage session, supplemented by lead API) ──
  const eventLabel   = EVENT_TYPES.find(e => e.value === s?.event_type?.value)?.label
                       || EVENT_TYPES.find(e => e.value === activeLead?.event_intent?.event_type)?.label
                       || s?.event_type?.value || activeLead?.event_intent?.event_type || '—';
  const city         = s ? (getCityDisplay(s) || '—') : (activeLead?.event_intent?.event_city ? (activeLead.event_intent.event_city.charAt(0) + activeLead.event_intent.event_city.slice(1).toLowerCase()) : '—');
  const guests       = GUEST_RANGES.find(g => g.value === s?.guest_count_range?.value)?.display
                       || GUEST_RANGES.find(g => g.value === activeLead?.event_intent?.guest_count_range)?.display || '—';
  const venueStatus  = VENUE_STATUSES.find(v => v.value === s?.venue_status?.value)?.label
                       || VENUE_STATUSES.find(v => v.value === activeLead?.event_intent?.venue_status)?.label || '—';
  const budget       = s?.budget_range?.display
                       || BUDGET_RANGES.find(b => b.value === s?.budget_range?.value)?.display
                       || BUDGET_RANGES.find(b => b.value === activeLead?.event_intent?.budget_range)?.display || '—';
  const services     = s?.service_interests?.value || [];
  const styleVals    = s?.style_preferences?.value || [];
  const styles       = styleVals.map(v => STYLE_OPTIONS.find(o => o.value === v)?.label || v);
  const funcVals     = s?.wedding_functions?.value || [];
  const functions    = funcVals.map(v => WEDDING_FUNCTIONS.find(f => f.value === v)?.label || v);
  const date         = s?.event_date?.display
                       || (s?.event_date?.value ? formatEventDateShort(s.event_date.value) : null)
                       || (activeLead?.event_intent?.event_date ? formatEventDateShort(activeLead.event_intent.event_date) : '—');
  const completion   = computeCompletionScore(s);
  const isComplete   = s?.journey_step === 'RESULT' || completion >= 100;
  const contactName  = s?.contact?.name   || activeLead?.contact_name  || 'Guest';
  const contactPhone = s?.contact?.mobile || activeLead?.contact_phone;

  // ── Loading screen ────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:PJ.ivory }}>
        <style>{DEMO_STYLES}</style>
        <RefreshCw size={20} style={{ color:PJ.slate, animation:'spin 1s linear infinite' }} />
      </div>
    );
  }

  // ── Header (shared) ───────────────────────────────────────────────────────
  const Header = () => (
    <div style={{ background:PJ.obsidian, padding:'16px max(24px, 5vw)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'16px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'16px' }}>
        <button onClick={() => navigate('/home')} style={{ background:'none', border:'none', cursor:'pointer', color:`${PJ.white}70`, display:'flex', alignItems:'center', gap:'6px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.8rem', padding:'4px 0' }}>
          <ChevronLeft size={15} /> Back
        </button>
        <div style={{ width:'1px', height:'16px', background:`${PJ.white}20` }} />
        <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:PJ.gold }}>
          ONE Engage · Journey Validation
        </p>
        <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.6rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', padding:'3px 8px', borderRadius:'100px', background:`#2E7D4F30`, color:'#2E7D4F', border:`1px solid #2E7D4F50` }}>
          INTERNAL · crm.lead.read
        </span>
      </div>
      <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', color:`${PJ.white}60` }}>
        Authorized internal users only
      </p>
    </div>
  );

  // ── Empty state (no localStorage session — show lookup bar) ──────────────
  if (!sessionState && !lookupResult) {
    return (
      <div style={{ fontFamily:"'DM Sans', sans-serif", background:PJ.ivory, minHeight:'100vh' }}>
        <style>{DEMO_STYLES}</style>
        <Header />
        <div style={{ maxWidth:'600px', margin:'0 auto', padding:'48px max(24px, 5vw)' }}>
          <EmptyState navigate={navigate} />
          {/* Lookup bar in empty state — look up any journey by ID */}
          <div style={{ marginTop:'32px' }}>
            <p style={{ margin:'0 0 10px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', fontWeight:600, letterSpacing:'0.08em', textTransform:'uppercase', color:PJ.slate, textAlign:'center' }}>
              Or look up an existing journey
            </p>
            <LookupBar onResult={handleLookupResult} onError={setLookupError} />
            {lookupError && (
              <div style={{ marginTop:'10px', display:'flex', gap:'8px', background:`${PJ.ruby}08`, border:`1px solid ${PJ.ruby}30`, borderRadius:'10px', padding:'10px 14px' }}>
                <AlertTriangle size={14} style={{ color:PJ.ruby, flexShrink:0, marginTop:'1px' }} />
                <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', color:PJ.ruby }}>{lookupError}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Main view ─────────────────────────────────────────────────────────────
  return (
    <div data-testid="demo-one-engage-page" style={{ fontFamily:"'DM Sans', sans-serif", background:PJ.ivory, minHeight:'100vh' }}>
      <style>{DEMO_STYLES}</style>
      <Header />

      {/* Lookup bar — always visible to authorized users */}
      <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'20px max(24px, 5vw) 0' }}>
        <LookupBar onResult={handleLookupResult} onError={setLookupError} />
        {lookupError && (
          <div style={{ marginTop:'8px', display:'flex', gap:'8px', background:`${PJ.ruby}08`, border:`1px solid ${PJ.ruby}30`, borderRadius:'10px', padding:'10px 14px' }}>
            <AlertTriangle size={14} style={{ color:PJ.ruby, flexShrink:0, marginTop:'1px' }} />
            <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', color:PJ.ruby }}>{lookupError}</p>
          </div>
        )}
        {isLookupMode && (
          <div style={{ marginTop:'8px', display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 12px', background:`${PJ.forest}08`, border:`1px solid ${PJ.forest}20`, borderRadius:'10px' }}>
            <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.75rem', color:PJ.forest }}>
              Showing looked-up lead: <strong>{activeLead?.lead_number || activeLeadId}</strong> — sourced from authenticated backend API
            </p>
            <button
              onClick={() => { setLookupResult(null); setLookupError(null); }}
              style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate, textDecoration:'underline' }}
            >
              Clear lookup
            </button>
          </div>
        )}
      </div>

      <div style={{ maxWidth:'1100px', margin:'0 auto', padding:'20px max(24px, 5vw) 32px', display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'24px' }}>

        {/* ── Left: Journey data ── */}
        <div className="demo-fade" style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
          {/* Show CRM sync status for localStorage journey (not for looked-up leads) */}
          {!isLookupMode && <CrmSyncBadge syncState={crmSync} leadId={crmSync?.crm_lead_id} />}
          {!isLookupMode && !isSynced && <SessionOnlyNotice />}
          {apiError && !isLookupMode && (
            <div style={{ display:'flex', gap:'10px', background:`${PJ.ruby}08`, border:`1px solid ${PJ.ruby}30`, borderRadius:'12px', padding:'12px 16px' }}>
              <AlertTriangle size={15} style={{ color:PJ.ruby, flexShrink:0, marginTop:'2px' }} />
              <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', color:PJ.ruby }}>{apiError}</p>
            </div>
          )}

          {/* Contact card — uses localStorage data supplemented by API */}
          <div data-testid="demo-contact-card" style={{ background:PJ.white, borderRadius:'20px', padding:'24px', border:`1px solid ${PJ.sand}` }}>
            <div style={{ display:'flex', alignItems:'center', gap:'14px', marginBottom:'20px' }}>
              <div style={{ width:'48px', height:'48px', borderRadius:'50%', flexShrink:0, background:`${PJ.gold}20`, display:'flex', alignItems:'center', justifyContent:'center', border:`2px solid ${PJ.gold}40` }}>
                <span style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:'1.4rem', fontWeight:500, color:PJ.gold }}>
                  {(contactName[0] || '?').toUpperCase()}
                </span>
              </div>
              <div>
                <p style={{ margin:0, fontFamily:"'Cormorant Garamond', serif", fontSize:'1.3rem', fontWeight:400, color:PJ.obsidian }}>
                  {contactName}
                </p>
                {contactPhone && (
                  <p style={{ margin:'3px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.8rem', color:PJ.slate }}>
                    {contactPhone}
                  </p>
                )}
              </div>
            </div>
            <div style={{ display:'flex', gap:'8px', flexWrap:'wrap', marginBottom:'16px' }}>
              <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.08em', padding:'4px 10px', borderRadius:'100px', background:`${PJ.ruby}12`, color:PJ.ruby, border:`1px solid ${PJ.ruby}30` }}>
                {(s?.source_touchpoint || activeLead?.lead_source || 'BUILD_MY_EVENT').replace(/_/g, ' ')}
              </span>
              <span style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.08em', padding:'4px 10px', borderRadius:'100px', background:`#8B691414`, color:'#8B6914', border:`1px solid ${PJ.gold}40` }}>
                {isLookupMode ? (activeLead?.status || 'LEAD') : isSynced ? 'PROVISIONAL LEAD' : 'AWAITING CRM SYNC'}
              </span>
            </div>
            <div style={{ display:'flex', flexDirection:'column' }}>
              <StructuredField label="Event"          value={eventLabel}  source={s?.event_type?.source} />
              <StructuredField label="Event Date"     value={date}        source={s?.event_date?.source} color={PJ.forest} />
              <StructuredField label="Location"       value={city}        source={s?.city?.source} />
              <StructuredField label="Guests"         value={guests}      source={s?.guest_count_range?.source} />
              <StructuredField label="Planning Scope" value={s?.planning_scope?.value === 'END_TO_END' ? 'End-to-End' : s?.planning_scope?.value ? 'Custom Selection' : null} source={s?.planning_scope?.source} />
              <StructuredField label="Venue"          value={venueStatus} source={s?.venue_status?.source} />
              <StructuredField label="Budget Range"   value={budget}      source={s?.budget_range?.source} />
            </div>
          </div>

          {/* Questionnaire completion (only for localStorage session) */}
          {s && (
            <div data-testid="demo-completion-card" style={{ background:PJ.white, borderRadius:'20px', padding:'20px 24px', border:`1px solid ${PJ.sand}` }}>
              <CompletionBar pct={completion} isComplete={isComplete} />
            </div>
          )}

          {s && <SourceAttributionCard state={s} />}

          {/* Services / styles / functions (from localStorage session) */}
          {s && (services.length > 0 || styles.length > 0 || functions.length > 0) && (
            <div style={{ background:PJ.white, borderRadius:'20px', padding:'20px 24px', border:`1px solid ${PJ.sand}`, display:'flex', flexDirection:'column', gap:'16px' }}>
              {functions.length > 0 && (
                <div>
                  <p style={{ margin:'0 0 8px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>Wedding Functions</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                    {functions.map(f => <span key={f} style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', padding:'4px 10px', borderRadius:'100px', background:PJ.blush, color:PJ.obsidian, border:`1px solid ${PJ.gold}40` }}>{f}</span>)}
                  </div>
                </div>
              )}
              {services.length > 0 && (
                <div>
                  <p style={{ margin:'0 0 8px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>Services</p>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px' }}>
                    {services.map(sv => <span key={sv} style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.78rem', padding:'4px 10px', borderRadius:'100px', background:PJ.ivory, border:`1px solid ${PJ.sand}`, color:PJ.obsidian }}>{getServiceLabel(sv)}</span>)}
                  </div>
                </div>
              )}
              {styles.length > 0 && (
                <div>
                  <p style={{ margin:'0 0 8px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>Style Direction</p>
                  <p style={{ margin:0, fontFamily:"'Cormorant Garamond', serif", fontSize:'1.05rem', fontStyle:'italic', color:PJ.obsidian }}>{styles.join(' · ')}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right: Real activity timeline + CRM record ── */}
        <div className="demo-fade" style={{ display:'flex', flexDirection:'column', gap:'16px', animationDelay:'0.1s' }}>

          {/* Activity timeline — real DB records */}
          <div data-testid="demo-timeline-section" style={{ background:PJ.white, borderRadius:'20px', border:`1px solid ${PJ.sand}`, overflow:'hidden' }}>
            <div style={{ padding:'20px 24px 14px', borderBottom:`1px solid ${PJ.sand}` }}>
              <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>
                Activity Timeline
              </p>
              <p style={{ margin:'4px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.slate }}>
                {isSynced
                  ? `Persisted activity records from CRM — ${activeTimeline?.total ?? (activeTimeline?.activities?.length ?? '…')} event(s)`
                  : 'Activity records are available once the journey is persisted to CRM'}
              </p>
              {/* Fix C: Timeline accuracy clarification */}
              {isSynced && (
                <p style={{ margin:'6px 0 0', fontFamily:"'DM Sans', sans-serif", fontSize:'0.68rem', color:PJ.slate, fontStyle:'italic', borderTop:`1px solid ${PJ.sand}`, paddingTop:'6px' }}>
                  Showing persisted records only. Questionnaire field selections are captured as a single LEAD_CREATED event — individual selection times are not separately recorded.
                </p>
              )}
            </div>
            {isSynced ? (
              <RealTimeline loading={timelineLoading} timeline={activeTimeline} />
            ) : (
              <div style={{ padding:'32px 24px', textAlign:'center' }}>
                <Clock size={24} style={{ color:PJ.sand, marginBottom:'12px' }} />
                <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.85rem', color:PJ.slate, margin:'0 0 6px' }}>No recorded activity yet.</p>
                <p style={{ fontFamily:"'DM Sans', sans-serif", fontSize:'0.75rem', color:PJ.slate }}>Complete the Build My Event journey to create a CRM lead, then return here to view the authoritative activity timeline.</p>
              </div>
            )}
          </div>

          {/* CRM lead record reference */}
          {(isSynced && activeLeadId) && (
            <div data-testid="demo-crm-record-card" style={{ background:PJ.white, borderRadius:'20px', padding:'20px 24px', border:`1px solid ${PJ.sand}` }}>
              <p style={{ margin:'0 0 14px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.65rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:PJ.slate }}>CRM Record</p>
              <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                {[
                  { label:'Lead Number',    value: crmSync?.lead_number || activeLead?.lead_number || '—' },
                  { label:'Lead ID',        value: activeLeadId, mono:true },
                  { label:'Session ID',     value: crmSync?.journey_session_id || activeLead?.journey_session_id, mono:true },
                  ...(activeLead ? [
                    { label:'Status',       value: activeLead.status },
                    { label:'Intake State', value: activeLead.intake_state || '—' },
                    { label:'Created At',   value: activeLead.created_at ? new Date(activeLead.created_at).toLocaleString('en-IN') : '—' },
                  ] : []),
                ].filter(r => r.value).map(({ label, value, mono }) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', gap:'12px', fontSize:'0.82rem', fontFamily:"'DM Sans', sans-serif" }}>
                    <span style={{ color:PJ.slate, flexShrink:0 }}>{label}</span>
                    <span style={{ fontWeight:mono?400:600, fontFamily:mono?'monospace':"'DM Sans', sans-serif", fontSize:mono?'0.68rem':'0.82rem', color:PJ.obsidian, wordBreak:'break-all', textAlign:'right' }}>{value}</span>
                  </div>
                ))}
              </div>
              {activeLead && (
                <div style={{ marginTop:'12px', padding:'8px 12px', background:`${PJ.forest}08`, border:`1px solid ${PJ.forest}20`, borderRadius:'8px' }}>
                  <p style={{ margin:0, fontFamily:"'DM Sans', sans-serif", fontSize:'0.72rem', color:PJ.forest }}>
                    Lead record verified in MongoDB. Timestamps are server-generated and authoritative.
                  </p>
                </div>
              )}
              <Link
                to={`/engage/leads/${activeLeadId}`}
                data-testid="demo-open-crm-lead"
                style={{ marginTop:'16px', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', background:PJ.obsidian, color:PJ.white, borderRadius:'100px', padding:'10px 20px', fontFamily:"'DM Sans', sans-serif", fontSize:'0.82rem', fontWeight:600, textDecoration:'none' }}
              >
                Open Full Lead in CRM <ExternalLink size={13} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
